import React, { useState } from "react";
import axios from "axios";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { BookOpen, CheckCircle2, Loader2, ShieldAlert, Receipt, FileDown, Package } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import ShippingInfoModal, { ShippingInfoButton } from "../components/ShippingInfoModal";
import useDownloadPdf from "../hooks/useDownloadPdf";
import useToolPageCms from "../hooks/useToolPageCms";
import { ToolPageBanner, ToolPageCtaBand } from "../components/ToolPageCms";
import PublicBookingItemsEditor from "../components/public/PublicBookingItemsEditor";
import { validateAllItems as validateBookingItems } from "../components/admin/sections/BookingItemsEditor";
import { SUPPORTED_CURRENCIES } from "../lib/currencies";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_CMS = {
  page_banner: {
    eyebrow: "Boekingsformulier",
    title: "Boek uw zending",
    highlight: "online",
    subtitle:
      "Boek direct uw zending: afzender, ontvanger, pakketten of pallets, eventueel met pickup en bewaakte verwerking.",
    badges: [
      { icon: "package", label: "Pakketten, pallets, verhuizing" },
      { icon: "truck",   label: "Pickup optie heel NL" },
      { icon: "receipt", label: "Optioneel factuur erbij" },
    ],
  },
  intro: {},
  cta_band: {
    title: "Boeking klaar?",
    description: "Wij sturen u binnen 1 werkdag een bevestiging met instructies.",
    cta_primary_label: "Vraag offerte aan",
    cta_primary_url: "/contact?get-quote=1#quote-form",
    cta_secondary_label: "Bel ons",
    cta_secondary_url: "tel:+31488235999",
  },
};

export default function BoekingsformulierPage() {
  const { t } = useI18n();
  const { data: cms } = useToolPageCms("boekingsformulier", FALLBACK_CMS);
  const [f, setF] = useState({
    first_name: "", last_name: "", company_name: "", email: "", phone: "", whatsapp: "",
    sender_address: "", sender_postcode: "", sender_city: "", sender_country: "Nederland",
    shipment_type: "pakket", destination: "Curaçao", method: "zeevracht",
    boxes: "", pallets: "", estimated_weight: "", estimated_cbm: "",
    goods_description: "", goods_value: "",
    pickup_required: false, pickup_address: "", pickup_datetime: "",
    recipient_name: "", recipient_company: "", recipient_email: "", recipient_phone: "",
    recipient_address: "", recipient_city: "", recipient_country: "Curaçao", recipient_instructions: "",
    truthful_data: false, consent_contact: false, dangerous_goods_confirmed: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  // Phase 18.8 — legacy single-item invoice checkbox (blijft voor backwards compat)
  const [generateInvoice, setGenerateInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const invoicePdf = useDownloadPdf();

  // Sprint 3a 6 Mrt 2026 — multi-items + documentenset
  const [items, setItems] = useState([]);
  const [wantsDocumentSet, setWantsDocumentSet] = useState(false);
  const [currency, setCurrency] = useState("EUR");

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setF((s) => ({ ...s, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.truthful_data || !f.consent_contact) {
      setError(t("forms.consents_required_error"));
      return;
    }
    if (!f.dangerous_goods_confirmed) {
      setError(t("dangerous_goods.checkbox_required_error"));
      return;
    }
    // Sprint 3a — items validatie: halflege regels blokkeren verzending.
    if (items.length > 0) {
      const errors = validateBookingItems(items, wantsDocumentSet);
      if (errors.length) {
        const first = errors[0];
        setError(`Artikel ${first.index + 1}: vul deze regel volledig in of verwijder de regel. Ontbrekend: ${first.missing.join(", ")}.`);
        window.scrollTo({ top: 350, behavior: "smooth" });
        return;
      }
    }
    if (wantsDocumentSet && items.length === 0) {
      setError("Voeg minimaal één artikel toe om een documentenset te kunnen aanvragen.");
      window.scrollTo({ top: 350, behavior: "smooth" });
      return;
    }
    // Phase 18.8 — legacy invoice-checkbox path: goods_value verplicht
    // (alleen gebruikt wanneer géén items zijn ingevuld).
    if (generateInvoice && items.length === 0 && (!f.goods_value || Number(f.goods_value) <= 0)) {
      setError(t("boeking.invoice_goods_value_required"));
      return;
    }
    setSubmitting(true);
    try {
      // Sprint 3a — sanitize items numerieke velden (empty string → null of Number)
      // zodat Pydantic float-parsing niet faalt op optionele lege velden.
      const sanitizedItems = items.length > 0
        ? items.map((it) => ({
            description: (it.description || "").trim(),
            quantity: it.quantity === "" || it.quantity == null ? 0 : Number(it.quantity),
            length: it.length === "" || it.length == null ? 0 : Number(it.length),
            width: it.width === "" || it.width == null ? 0 : Number(it.width),
            height: it.height === "" || it.height == null ? 0 : Number(it.height),
            unit: it.unit || "cm",
            weight: it.weight === "" || it.weight == null ? null : Number(it.weight),
            value_per_unit: it.value_per_unit === "" || it.value_per_unit == null
              ? null : Number(it.value_per_unit),
            condition: it.condition || null,
            hs_code: (it.hs_code || "").trim() || null,
            country_of_origin: (it.country_of_origin || "").trim() || null,
            note: (it.note || "").trim() || null,
          }))
        : null;
      const payload = {
        ...f,
        boxes: f.boxes === "" ? null : parseInt(f.boxes, 10),
        pallets: f.pallets === "" ? null : parseInt(f.pallets, 10),
        estimated_weight: f.estimated_weight === "" ? null : Number(f.estimated_weight),
        estimated_cbm: f.estimated_cbm === "" ? null : Number(f.estimated_cbm),
        goods_value: f.goods_value === "" ? null : Number(f.goods_value),
        // Legacy single-item invoice OF nieuwe documentenset (impliciete value-invoice)
        generate_invoice: !!generateInvoice || !!wantsDocumentSet,
        // Sprint 3a — multi-items payload
        items: sanitizedItems,
        wants_document_set: !!wantsDocumentSet,
        wants_value_invoice: !!wantsDocumentSet,
        value_invoice_currency: currency,
      };
      const r = await axios.post(`${BACKEND_URL}/api/booking-form`, payload);
      setDone(true);
      setInvoiceResult(r.data?.invoice || null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // Sprint 3a — Pydantic 422 stuurt detail als array van objecten.
      // Zet die om naar een leesbare Nederlandse foutstring i.p.v. React
      // rechtstreeks een object te laten renderen (dat crasht).
      const detail = err?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc.slice(1).join(".") : "";
            return field ? `${field}: ${d.msg}` : d.msg;
          })
          .filter(Boolean)
          .join(" · ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object" && detail.msg) {
        msg = detail.msg;
      } else {
        msg = t("common.try_again");
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvoice = () =>
    invoicePdf.download(
      async () => {
        if (!invoiceResult?.download_url) {
          throw new Error("missing download URL");
        }
        const r = await axios.get(`${BACKEND_URL}${invoiceResult.download_url}`, { responseType: "blob" });
        return {
          blob: new Blob([r.data], { type: "application/pdf" }),
          filename: `${invoiceResult.invoice_number}.pdf`,
        };
      },
    );

  return (
    <>
      <SEO title={`${t("boeking.page_title")} | 5999Cargo`} description={t("boeking.page_subtitle")} />
      <ShippingInfoModal autoOpen />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="boekingsformulier-page">
        <ToolPageBanner
          banner={cms?.page_banner || FALLBACK_CMS.page_banner}
          image="https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          imageAlt="Warehouse medewerker bij pakketten — boeking 5999Cargo"
          eyebrowIcon={BookOpen}
          testid="boekingsformulier-hero"
          extras={
            <ShippingInfoButton testId="boeking-shipping-info-trigger" className="text-white border-white/30 hover:border-white" />
          }
        />

        <section className="py-14">
          <div className="container-narrow">
            {done ? (
              <div className="space-y-4" data-testid="boeking-success">
                <div className="card-premium p-8 border-l-4 border-l-emerald-500">
                  <div className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6" />
                    </span>
                    <div>
                      <h2 className="font-chivo font-bold text-2xl text-emerald-900">{t("boeking.success_title")}</h2>
                      <p className="text-[15px] text-emerald-800 mt-1.5 leading-relaxed">{t("boeking.success_desc")}</p>
                    </div>
                  </div>
                </div>

                {/* Phase 18.8 — invoice download block */}
                {invoiceResult?.status === "generated" && (
                  <div className="card-premium p-6 border-l-4 border-l-brand-500" data-testid="boeking-invoice-success">
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
                        <Receipt className="w-6 h-6" />
                      </span>
                      <div className="flex-1">
                        <h3 className="font-chivo font-bold text-lg text-brand-900">{t("boeking.invoice_generated_title")}</h3>
                        <p className="text-sm text-ink-600 mt-1">{t("boeking.invoice_generated_intro")}</p>
                        <div className="mt-3 inline-flex items-center gap-2 bg-brand-50 border border-brand-100 rounded-lg px-3 py-1.5">
                          <span className="text-[10px] uppercase tracking-[0.18em] font-chivo font-bold text-brand-500">{t("boeking.invoice_number_label")}</span>
                          <span className="font-mono text-sm font-bold text-brand-900" data-testid="boeking-invoice-number">{invoiceResult.invoice_number}</span>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <Button
                            onClick={downloadInvoice}
                            disabled={invoicePdf.loading}
                            className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-chivo font-bold uppercase text-xs px-5 py-2.5"
                            data-testid="boeking-invoice-download"
                          >
                            {invoicePdf.loading
                              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("boeking.invoice_downloading")}</>
                              : <><FileDown className="w-4 h-4 mr-2" /> {t("boeking.invoice_download_btn")}</>}
                          </Button>
                          {invoicePdf.loading && invoicePdf.isSlow && (
                            <span className="text-xs text-ink-500">{invoicePdf.slowHint}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {invoiceResult?.status === "failed" && (
                  <div className="card-premium p-6 border-l-4 border-l-amber-500" data-testid="boeking-invoice-failed">
                    <h3 className="font-chivo font-bold text-amber-900">{t("boeking.invoice_failed_title")}</h3>
                    <p className="text-sm text-amber-800 mt-1">{invoiceResult.error || t("boeking.invoice_failed_generic")}</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="boekingsformulier-form">
                {/* Verzender */}
                <div className="card-premium p-6 sm:p-8" data-testid="boeking-sender-block">
                  <h2 className="font-chivo font-bold text-xl text-brand-900 mb-5">{t("forms.sender_block_title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>{t("forms.first_name")} *</Label><Input data-testid="boeking-first-name" required value={f.first_name} onChange={set("first_name")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.last_name")} *</Label><Input data-testid="boeking-last-name" required value={f.last_name} onChange={set("last_name")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.company_optional")}</Label><Input data-testid="boeking-company" value={f.company_name} onChange={set("company_name")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.email")} *</Label><Input data-testid="boeking-email" required type="email" value={f.email} onChange={set("email")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.phone")} *</Label><Input data-testid="boeking-phone" required value={f.phone} onChange={set("phone")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.whatsapp_optional")}</Label><Input data-testid="boeking-whatsapp" value={f.whatsapp} onChange={set("whatsapp")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.sender_address")}</Label><Input data-testid="boeking-sender-address" value={f.sender_address} onChange={set("sender_address")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.postcode")}</Label><Input data-testid="boeking-sender-postcode" value={f.sender_postcode} onChange={set("sender_postcode")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.city")}</Label><Input data-testid="boeking-sender-city" value={f.sender_city} onChange={set("sender_city")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.country")}</Label><Input data-testid="boeking-sender-country" value={f.sender_country} onChange={set("sender_country")} className="rounded-xl" /></div>
                  </div>
                </div>

                {/* Ontvanger */}
                <div className="bg-white border border-gray-200 p-5" data-testid="boeking-receiver-block">
                  <h2 className="font-chivo font-bold text-lg text-[#082c50] mb-3">{t("forms.receiver_block_title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>{t("forms.recipient_name")} *</Label><Input data-testid="boeking-recipient-name" required value={f.recipient_name} onChange={set("recipient_name")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.company_optional")}</Label><Input data-testid="boeking-recipient-company" value={f.recipient_company} onChange={set("recipient_company")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.recipient_email_optional")}</Label><Input data-testid="boeking-recipient-email" type="email" value={f.recipient_email} onChange={set("recipient_email")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.phone")} *</Label><Input data-testid="boeking-recipient-phone" required value={f.recipient_phone} onChange={set("recipient_phone")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.destination_address")} *</Label><Input data-testid="boeking-recipient-address" required value={f.recipient_address} onChange={set("recipient_address")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.destination_city")}</Label><Input data-testid="boeking-recipient-city" value={f.recipient_city} onChange={set("recipient_city")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.destination_country")} *</Label><Input data-testid="boeking-recipient-country" required value={f.recipient_country} onChange={set("recipient_country")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.extra_instructions_optional")}</Label><Textarea data-testid="boeking-recipient-instructions" rows={2} value={f.recipient_instructions} onChange={set("recipient_instructions")} className="rounded-xl" /></div>
                  </div>
                </div>

                {/* Zending */}
                <div className="card-premium p-6 sm:p-8">
                  <h2 className="font-chivo font-bold text-xl text-brand-900 mb-5">{t("forms.shipment_block_title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>{t("boeking.shipment_type")} *</Label>
                      <select data-testid="boeking-type" required value={f.shipment_type} onChange={set("shipment_type")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
                        <option value="pakket">{t("boeking.type_pakket")}</option>
                        <option value="pallet">{t("boeking.type_pallet")}</option>
                        <option value="dozen">{t("boeking.type_groupage")}</option>
                        <option value="verhuizing">{t("boeking.type_verhuizing")}</option>
                      </select>
                    </div>
                    <div><Label>{t("forms.destination")} *</Label><Input data-testid="boeking-destination" required value={f.destination} onChange={set("destination")} className="rounded-xl" /></div>
                    <div>
                      <Label>{t("forms.shipment_method")}</Label>
                      <select data-testid="boeking-method" value={f.method} onChange={set("method")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
                        <option value="zeevracht">{t("paklijst.method_zee")}</option>
                        <option value="luchtvracht">{t("paklijst.method_lucht")}</option>
                        <option value="shop-drop-ship">{t("paklijst.method_shop_drop_ship")}</option>
                      </select>
                    </div>
                    <div></div>
                    <div><Label>{t("boeking.boxes")}</Label><Input data-testid="boeking-boxes" type="number" min="0" value={f.boxes} onChange={set("boxes")} className="rounded-xl" /></div>
                    <div><Label>{t("boeking.pallets")}</Label><Input data-testid="boeking-pallets" type="number" min="0" value={f.pallets} onChange={set("pallets")} className="rounded-xl" /></div>
                    <div><Label>{t("boeking.estimated_weight")}</Label><Input data-testid="boeking-weight" type="number" step="0.1" min="0" value={f.estimated_weight} onChange={set("estimated_weight")} className="rounded-xl" /></div>
                    <div><Label>{t("boeking.estimated_cbm")}</Label><Input data-testid="boeking-cbm" type="number" step="0.01" min="0" value={f.estimated_cbm} onChange={set("estimated_cbm")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("boeking.goods_description")}</Label><Textarea data-testid="boeking-goods" rows={3} value={f.goods_description} onChange={set("goods_description")} className="rounded-xl" /></div>
                    <div><Label>{t("boeking.goods_value")}</Label><Input data-testid="boeking-goods-value" type="number" step="0.01" min="0" value={f.goods_value} onChange={set("goods_value")} className="rounded-xl" /></div>
                  </div>
                </div>

                {/* Sprint 3a — Uw artikelen (multi-items) */}
                <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6" data-testid="boeking-items-section">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center">
                      <Package className="w-5 h-5" />
                    </span>
                    <div className="flex-1">
                      <h2 className="font-chivo font-bold text-lg text-[#082c50]">Uw artikelen</h2>
                      <p className="text-xs text-ink-600 mt-0.5">
                        Voeg alle artikelen toe die u wilt versturen. Elk artikel heeft afmetingen (L × B × H). Voeg er zoveel toe als nodig — u kunt regels dupliceren om snel te werken.
                      </p>
                    </div>
                  </div>
                  <PublicBookingItemsEditor
                    items={items}
                    onChange={setItems}
                    currency={currency}
                    wantsValueInvoice={wantsDocumentSet}
                  />

                  {/* Documentenset checkbox — direct onder items */}
                  <div className="mt-5 rounded-2xl bg-brand-50 border-l-4 border-brand-500 p-4 sm:p-5"
                       data-testid="boeking-document-set-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={wantsDocumentSet}
                        onChange={(e) => setWantsDocumentSet(e.target.checked)}
                        className="mt-1 h-5 w-5 accent-brand-600 cursor-pointer"
                        disabled={items.length === 0}
                        data-testid="boeking-wants-document-set"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-brand-600" />
                          <span className="font-chivo font-bold text-sm text-brand-900">Documentenset aanvragen</span>
                        </div>
                        <p className="text-xs text-ink-700 mt-1 leading-relaxed">
                          Wanneer u deze optie aanvinkt, maken wij op basis van uw ingevulde artikelen automatisch een <strong>Boekingsformulier</strong>, <strong>Paklijst</strong> en <strong>Waardefactuur / Value Invoice</strong> aan. U ontvangt de documenten per e-mail zodra ze klaar zijn.
                        </p>
                        {items.length === 0 && (
                          <p className="text-[11px] text-brand-800 mt-2 italic">
                            Voeg eerst minimaal één artikel toe om deze optie te kunnen aanvinken.
                          </p>
                        )}
                        {wantsDocumentSet && (
                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            <label className="text-xs font-semibold text-ink-700">Valuta voor waardefactuur:</label>
                            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                                    className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm"
                                    data-testid="boeking-document-set-currency">
                              {SUPPORTED_CURRENCIES.map((c) =>
                                <option key={c} value={c}>{c}</option>
                              )}
                            </select>
                            <span className="text-[11px] text-ink-600">
                              Waarde per stuk wordt verplicht op elk artikel.
                            </span>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Pickup */}
                <div className="bg-white border border-gray-200 p-5">
                  <h2 className="font-chivo font-bold text-lg text-[#082c50] mb-3">{t("boeking.pickup_section")}</h2>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid="boeking-pickup-required" checked={f.pickup_required} onCheckedChange={(v) => setF((s) => ({ ...s, pickup_required: !!v }))} />
                    {t("boeking.pickup_required_label")}
                  </label>
                  {f.pickup_required && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <div className="sm:col-span-2"><Label>{t("boeking.pickup_address")}</Label><Input data-testid="boeking-pickup-address" value={f.pickup_address} onChange={set("pickup_address")} className="rounded-xl" /></div>
                      <div className="sm:col-span-2"><Label>{t("boeking.pickup_datetime")}</Label><Input data-testid="boeking-pickup-datetime" value={f.pickup_datetime} onChange={set("pickup_datetime")} className="rounded-xl" /></div>
                    </div>
                  )}
                </div>

                {/* Phase 18.8 — Legacy single-item invoice checkbox. Alleen
                    tonen wanneer géén items zijn ingevuld (documentenset
                    checkbox verzorgt de nieuwe multi-item flow). */}
                {items.length === 0 && (
                <div
                  className="rounded-2xl bg-brand-50 border-l-4 border-brand-500 p-5 sm:p-6"
                  data-testid="boeking-invoice-block"
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateInvoice}
                      onChange={(e) => setGenerateInvoice(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-brand-600"
                      data-testid="boeking-generate-invoice-checkbox"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-brand-500" />
                        <span className="font-chivo font-bold text-sm text-brand-900">{t("boeking.invoice_option_label")}</span>
                      </div>
                      <p className="text-xs text-ink-600 mt-1 leading-relaxed">
                        {t("boeking.invoice_option_help")}
                      </p>
                    </div>
                  </label>
                </div>
                )}

                {/* Dangerous Goods waarschuwing + verplichte akkoord */}
                <div className="rounded-2xl bg-red-50 border-l-4 border-red-500 p-5 sm:p-6" data-testid="boeking-dangerous-goods-block">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-chivo font-bold text-sm text-red-800">{t("dangerous_goods.boeking_warning_title")}</h3>
                      <p className="text-xs text-red-700 mt-1 leading-relaxed">
                        {t("dangerous_goods.warning_intro")}{" "}
                        <a href="/documenten#dangerous-goods" target="_blank" rel="noreferrer" className="underline font-bold">{t("dangerous_goods.full_list_link")}</a>.
                      </p>
                      <label className="flex items-start gap-2 mt-3 text-sm text-red-900 cursor-pointer">
                        <input
                          type="checkbox"
                          required
                          data-testid="boeking-dangerous-goods-checkbox"
                          checked={f.dangerous_goods_confirmed}
                          onChange={(e) => setF((s) => ({ ...s, dangerous_goods_confirmed: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 accent-red-700"
                        />
                        <span>
                          <span className="font-bold">{t("dangerous_goods.required_prefix")}:</span> {t("dangerous_goods.checkbox_label")}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Akkoord */}
                <div className="card-premium p-6 sm:p-8 space-y-3">
                  <h2 className="font-chivo font-bold text-xl text-brand-900">{t("boeking.consent_section")}</h2>
                  <label className="flex items-start gap-3 text-[14px] text-ink-700">
                    <Checkbox data-testid="boeking-truthful" checked={f.truthful_data} onCheckedChange={(v) => setF((s) => ({ ...s, truthful_data: !!v }))} />
                    <span>{t("forms.truthful_required")}</span>
                  </label>
                  <label className="flex items-start gap-3 text-[14px] text-ink-700">
                    <Checkbox data-testid="boeking-consent" checked={f.consent_contact} onCheckedChange={(v) => setF((s) => ({ ...s, consent_contact: !!v }))} />
                    <span>{t("forms.consent_required")}</span>
                  </label>
                </div>

                {error && (
                  <div data-testid="boeking-error" className="rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 text-sm">{error}</div>
                )}

                <Button type="submit" disabled={submitting} className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-chivo font-bold uppercase text-sm px-8 py-6 shadow-brand-soft hover:shadow-brand-glow active:scale-[0.98] transition-all" data-testid="boeking-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("boeking.submitting")}</> : t("boeking.submit")}
                </Button>
              </form>
            )}
          </div>
        </section>
        <ToolPageCtaBand cta={cms?.cta_band || FALLBACK_CMS.cta_band} testid="boekingsformulier-cta-band" />
      </main>
    </>
  );
}
